import { formatUZS } from "@elektr/shared";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ArrowLeft, ImageOff, MessageCircle, Pencil, Phone, Send } from "lucide-react";
import { useState } from "react";
import { Link, useParams } from "react-router-dom";

import { LeadDialog } from "@/components/catalog/LeadDialog";
import { Button } from "@/components/ui/button";
import { Badge, Card, Spinner } from "@/components/ui/card";
import { api, unwrap } from "@/lib/api";
import { isAdmin, useAuth } from "@/lib/auth";
import { QUALITY_KEYS, QUALITY_TONE } from "@/lib/catalog";
import { useT } from "@/lib/i18n";
import { cn } from "@/lib/utils";

const STAT_KEYS = [
  ["views", "catalog.stat_views"],
  ["calls", "catalog.stat_calls"],
  ["telegram", "catalog.stat_telegram"],
  ["leads", "catalog.stat_leads"],
] as const;

/** Mahsulot sahifasi: galereya, narx, xususiyatlar, aloqa tugmalari (har bir bosish analitikaga yoziladi). */
export function ProductPage({ mode }: { mode: "staff" | "customer" }) {
  const { id = "" } = useParams();
  const { t } = useT();
  const { user } = useAuth();
  const base = mode === "staff" ? "/catalog" : "/my/catalog";
  const [index, setIndex] = useState(0);
  const [lead, setLead] = useState(false);

  const query = useQuery({
    queryKey: ["product", id],
    queryFn: async () => unwrap(await api.GET("/api/products/{id}/", { params: { path: { id } } })),
  });
  const track = useMutation({
    mutationFn: async (kind: "call" | "telegram") => {
      await api.POST("/api/products/{id}/events/", { params: { path: { id } }, body: { kind } });
    },
  });

  if (query.isLoading) return <Spinner className="m-10 size-8" />;
  if (query.isError || !query.data) {
    return (
      <div className="p-10 text-center">
        <p className="mb-4 text-muted-foreground">{t("catalog.not_found")}</p>
        <Button asChild variant="outline">
          <Link to={base}>{t("catalog.back")}</Link>
        </Button>
      </div>
    );
  }
  const p = query.data;
  const images = p.images ?? [];
  const current = images[Math.min(index, Math.max(images.length - 1, 0))];
  const q = (p.quality ?? "standard") as keyof typeof QUALITY_KEYS;
  const canEdit = mode === "staff" && !!user && (isAdmin(user) || p.electrician === user.id);
  const specs = Object.entries((p.specs ?? {}) as Record<string, string | number>);

  return (
    <div className="flex flex-col gap-4">
      <Link
        to={base}
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:underline"
      >
        <ArrowLeft className="size-4" /> {t("catalog.back")}
      </Link>

      <div className="grid gap-4 md:grid-cols-2">
        <div className="flex flex-col gap-2">
          <div className="flex aspect-[4/3] items-center justify-center overflow-hidden rounded-xl border border-border bg-muted">
            {current?.preview_url ? (
              <img src={current.preview_url} alt={p.name} className="size-full object-contain" />
            ) : (
              <ImageOff className="size-10 text-muted-foreground" />
            )}
          </div>
          {images.length > 1 && (
            <div
              className="flex gap-2 overflow-x-auto"
              role="tablist"
              aria-label={t("catalog.gallery")}
            >
              {images.map((im, i) => (
                <button
                  key={im.id}
                  role="tab"
                  aria-selected={i === index}
                  aria-label={`${t("catalog.photo")} ${i + 1}`}
                  onClick={() => setIndex(i)}
                  className={cn(
                    "size-16 shrink-0 overflow-hidden rounded-lg border-2",
                    i === index ? "border-primary" : "border-transparent",
                  )}
                >
                  {im.thumb_url && (
                    <img src={im.thumb_url} alt="" className="size-full object-cover" />
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="flex flex-col gap-3">
          <div>
            <h1 className="text-2xl font-bold">{p.name}</h1>
            <p className="text-sm text-muted-foreground">
              {[p.brand, p.category_name].filter(Boolean).join(" · ")}
            </p>
          </div>
          <div className="flex flex-wrap items-baseline gap-3">
            <span className="text-3xl font-bold">{formatUZS(p.price_uzs)}</span>
            {p.old_price_uzs ? (
              <span className="text-lg text-muted-foreground line-through">
                {formatUZS(p.old_price_uzs)}
              </span>
            ) : null}
            {p.discount_percent ? <Badge tone="danger">-{p.discount_percent}%</Badge> : null}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Badge tone={QUALITY_TONE[q]}>{t(QUALITY_KEYS[q])}</Badge>
            <Badge tone={p.in_stock ? "success" : "danger"}>
              {p.in_stock ? t("catalog.in_stock") : t("catalog.out_of_stock")}
            </Badge>
            {!p.is_active && <Badge tone="neutral">{t("catalog.inactive")}</Badge>}
            {p.warranty_months ? (
              <span className="text-sm text-muted-foreground">
                {t("catalog.warranty", { n: p.warranty_months })}
              </span>
            ) : null}
          </div>

          <Card className="flex flex-col gap-3">
            <div>
              <div className="text-xs text-muted-foreground">{t("catalog.offered_by")}</div>
              <div className="font-semibold">{p.contact.name}</div>
            </div>
            <div className="flex flex-wrap gap-2">
              {p.contact.phone && (
                <Button asChild>
                  <a href={`tel:${p.contact.phone}`} onClick={() => track.mutate("call")}>
                    <Phone className="size-4" /> {t("catalog.call")}
                  </a>
                </Button>
              )}
              {p.contact.telegram_url && (
                <Button asChild variant="outline">
                  <a
                    href={p.contact.telegram_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => track.mutate("telegram")}
                  >
                    <Send className="size-4" /> {t("catalog.telegram")}
                  </a>
                </Button>
              )}
              <Button variant="outline" onClick={() => setLead(true)}>
                <MessageCircle className="size-4" /> {t("catalog.leave_request")}
              </Button>
            </div>
          </Card>

          {canEdit && (
            <Button asChild variant="outline" className="self-start">
              <Link to={`/catalog/${p.id}/edit`}>
                <Pencil className="size-4" /> {t("common.edit")}
              </Link>
            </Button>
          )}
        </div>
      </div>

      {p.stats && (
        <Card data-testid="product-stats">
          <h3 className="mb-2 font-semibold">{t("catalog.stats_title")}</h3>
          <dl className="grid grid-cols-4 gap-2 text-center">
            {STAT_KEYS.map(([k, key]) => (
              <div key={k}>
                <dd className="text-xl font-bold">{p.stats![k]}</dd>
                <dt className="text-xs text-muted-foreground">{t(key)}</dt>
              </div>
            ))}
          </dl>
        </Card>
      )}

      {p.purpose && (
        <Card>
          <h3 className="mb-1 font-semibold">{t("catalog.purpose")}</h3>
          <p className="whitespace-pre-line text-sm">{p.purpose}</p>
        </Card>
      )}
      {p.description && (
        <Card>
          <h3 className="mb-1 font-semibold">{t("catalog.description")}</h3>
          <p className="whitespace-pre-line text-sm">{p.description}</p>
        </Card>
      )}
      {specs.length > 0 && (
        <Card>
          <h3 className="mb-2 font-semibold">{t("catalog.specs")}</h3>
          <table className="w-full text-sm">
            <tbody>
              {specs.map(([k, v]) => (
                <tr key={k} className="border-b border-border last:border-0">
                  <th
                    scope="row"
                    className="w-1/2 py-2 pr-3 text-left font-normal text-muted-foreground"
                  >
                    {k}
                  </th>
                  <td className="py-2 font-medium">{String(v)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      <LeadDialog product={p} open={lead} onOpenChange={setLead} />
    </div>
  );
}
